import { Divider, HStack, Text, VStack } from "@chakra-ui/react";
import {
  UserAttributeCategoryFragment,
  UserAttributeFragment,
  UserFragment,
} from "gql/graphql";
import React, { memo, useMemo } from "react";
import colors from "app/colors";
import { useUserAttributes } from "modules/providers/user-attributes-provider";

interface UserAttributesByCategory {
  userAttributeCategory: UserAttributeCategoryFragment;
  userAttributes: UserAttributeFragment[];
}

interface IProps {
  user: UserFragment;
}

const UserAttributes = ({ user }: IProps) => {
  const { userAttributesById, userAttributeCategories } = useUserAttributes();

  const userAttributesByUserAttributeCategory = useMemo(() => {
    const userAttributesByCategory = [] as UserAttributesByCategory[];
    const userAttributes = user.userAttributeIdList
      .map((userAttributeId) => userAttributesById[userAttributeId])
      .filter((userAttribute) => !!userAttribute);

    userAttributeCategories.forEach((userAttributeCategory) => {
      const _userAttributes = userAttributes.filter(
        (userAttribute) =>
          userAttribute?.userAttributeCategoryId === userAttributeCategory.id,
      );
      if (_userAttributes.length > 0) {
        userAttributesByCategory.push({
          userAttributeCategory,
          userAttributes: _userAttributes as UserAttributeFragment[],
        });
      }
    });

    return userAttributesByCategory;
  }, [user.userAttributeIdList, userAttributeCategories, userAttributesById]);

  return (
    <VStack w="full" spacing={0} divider={<Divider />} alignItems="flex-start">
      {userAttributesByUserAttributeCategory.length === 0 && (
        <Text color={colors.gray50} fontSize={14} fontStyle="italic">
          No attributes assigned yet
        </Text>
      )}
      {userAttributesByUserAttributeCategory.map((userAttributesByCategory) => (
        <HStack
          spacing={0}
          w="full"
          key={userAttributesByCategory.userAttributeCategory.id}
          minH="48px"
        >
          <Text
            minW={140}
            maxW={140}
            color={colors.blackSecondary}
            fontWeight={700}
            fontSize={14}
          >
            {userAttributesByCategory.userAttributeCategory.name}
          </Text>
          <Text color={colors.gray50} fontWeight={500} fontSize={14}>
            {userAttributesByCategory.userAttributes
              .map((userAttribute) => userAttribute.name)
              .join(", ")
              .trim()}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
};

export default memo(UserAttributes);
